import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProperty } from "@/context/PropertyContext";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, User, FileText, Wallet, FileCheck, Printer, Download, CalendarCheck, Eye, Plus } from "lucide-react";
import { eur, fechaCorta } from "@/lib/format";
import { contratoToHtml, CONTRATO_CSS } from "@/lib/contractHtml";
import { tipoDocumentoLabel } from "@/pages/Tenants";
import { toast } from "sonner";

const PLAZO_FORMALIZAR_DIAS = 10;
const hoyIso = () => new Date().toISOString().slice(0, 10);
const sumarDias = (ymd, dias) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
};

// Datos que salen en el resguardo; sin ellos no se genera
const DATOS_RESERVA = [
  ["direccion", "dirección del piso"], ["arrendador_nombre", "nombre del arrendador"],
  ["arrendador_dni", "DNI del arrendador"], ["iban", "IBAN del piso"],
];
const faltanDatosReserva = (prop, tenant) => [
  ...DATOS_RESERVA.filter(([k]) => !String(prop?.[k] || "").trim()).map(([, label]) => label),
  ...(String(tenant?.dni || "").trim() ? [] : ["documento del inquilino"]),
];

const fullDocumentHtml = (html) => `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>Reserva de habitación</title><style>${CONTRATO_CSS}</style></head><body>${html}</body></html>`;

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectProperty } = useProperty();
  const [data, setData] = useState(null);
  const [prop, setProp] = useState(null);
  const [reserva, setReserva] = useState(null); // formulario del resguardo
  const [docHtml, setDocHtml] = useState("");
  const [pdfSaving, setPdfSaving] = useState(false);

  useEffect(() => {
    api.get(`/tenants/${id}`).then((r) => setData(r.data)).catch(() => navigate("/inquilinos"));
  }, [id, navigate]);

  const propertyId = data?.tenant?.property_id;
  useEffect(() => {
    if (propertyId) api.get(`/properties/${propertyId}`).then((r) => setProp(r.data)).catch(() => {});
  }, [propertyId]);

  const openReserva = () => {
    // Prioridad: contrato más reciente > última reserva guardada > valores por defecto del piso.
    const c = [...(data.contracts || [])].sort((a, b) => String(b.fecha_inicio).localeCompare(String(a.fecha_inicio)))[0];
    const r = data.tenant.reserva || {};
    const hab = c?.habitacion ?? r.habitacion ?? String(prop?.habitaciones?.[0]?.numero ?? "");
    const room = (prop?.habitaciones || []).find((h) => String(h.numero) === String(hab));
    const fechaReserva = r.fecha_reserva || hoyIso();
    setReserva({
      habitacion: String(hab || ""),
      fecha_entrada: (c?.fecha_inicio || r.fecha_entrada || "").slice(0, 10),
      meses: c?.meses ?? r.meses ?? 12,
      importe: c?.fianza ?? r.importe ?? prop?.fianza_default ?? 0,
      alquiler: c?.alquiler ?? r.alquiler ?? ((room?.precio ?? 0) + (prop?.gasto_mensual_default ?? 60)),
      fecha_limite: r.fecha_limite || sumarDias(fechaReserva, PLAZO_FORMALIZAR_DIAS),
      fecha_reserva: fechaReserva,
    });
  };

  // Sin argumento usa el formulario; con la reserva guardada sirve para volver a ver el resguardo.
  const generarReserva = async (datos = reserva) => {
    const faltan = faltanDatosReserva(prop, data.tenant);
    if (faltan.length) { toast.error(`Faltan datos para el resguardo: ${faltan.join(", ")}`); return; }
    if (!datos.fecha_entrada) { toast.error("Indica la fecha de entrada"); return; }
    try {
      const { data: res } = await api.post(`/tenants/${id}/reserva/documento`, {
        ...datos,
        meses: Number(datos.meses) || 12,
        importe: Number(datos.importe) || 0,
        alquiler: Number(datos.alquiler) || 0,
        fecha_limite: datos.fecha_limite || null,
      });
      setDocHtml(contratoToHtml(res.documento || "", { arrendador: prop?.arrendador_nombre || "", soloArrendador: true }));
      setData((d) => ({ ...d, tenant: { ...d.tenant, reserva: res.reserva } }));
      setReserva(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar el resguardo");
    }
  };

  const printDoc = () => {
    const w = window.open("", "_blank");
    w.document.write(fullDocumentHtml(docHtml));
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const openSignedPdf = async () => {
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.document.write("<p style='font-family: system-ui; padding: 24px;'>Generando PDF firmado...</p>");
      pdfWindow.document.close();
    }
    setPdfSaving(true);
    try {
      const t = data.tenant;
      const name = `${t.nombre} ${t.apellidos}`.normalize("NFD").replace(/[̀-ͯ]/g, "")
        .toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "INQUILINO";
      const { data: res } = await api.post("/contracts/documento/pdf-link", {
        html: fullDocumentHtml(docHtml), filename: `RESERVA_HABITACION_${name}.pdf`, solo_arrendador: true,
      });
      const url = `${API.replace(/\/api$/, "")}${res.url}`;
      if (pdfWindow) pdfWindow.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
      toast.error(e?.response?.data?.detail || "Error generando el PDF firmado");
    } finally {
      setPdfSaving(false);
    }
  };

  const setR = (k) => (e) => setReserva((r) => ({ ...r, [k]: e.target.value }));

  if (!data) return <div className="data-card p-10 text-center text-muted-foreground">Cargando…</div>;

  const { tenant, contracts, payments } = data;
  const today = new Date().toISOString().slice(0, 10);
  const totalCobrado = payments.filter((p) => p.realizado).reduce((s, p) => s + p.importe, 0);
  const vencidos = payments.filter((p) => !p.realizado && (p.fecha || "").slice(0, 10) <= today);
  const programados = payments.filter((p) => !p.realizado && (p.fecha || "").slice(0, 10) > today);
  const pendiente = vencidos.reduce((s, p) => s + p.importe, 0);
  const faltanDatosPiso = faltanDatosReserva(prop, tenant);
  const res = tenant.reserva;
  const estadoReserva = !res ? null
    : contracts.length > 0 ? { label: "Formalizada", cls: "bg-success/15 text-success hover:bg-success/15" }
      : res.fecha_limite && res.fecha_limite < today ? { label: "Plazo vencido", cls: "bg-destructive/15 text-destructive hover:bg-destructive/15" }
        : { label: "Pendiente de contrato", cls: "bg-warning/15 text-warning hover:bg-warning/15" };
  const crearContrato = () => {
    selectProperty(tenant.property_id);
    navigate("/contratos", { state: { nuevoContratoTenant: tenant.id } });
  };

  return (
    <div data-testid="tenant-detail-page">
      <button onClick={() => navigate("/inquilinos")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Volver a inquilinos
      </button>

      <PageHeader title={`${tenant.nombre} ${tenant.apellidos}`} subtitle={tenant.nacionalidad}>
        <Button variant="outline" onClick={openReserva} disabled={!prop} data-testid="reserva-button">
          <FileCheck className="h-4 w-4 mr-1.5" /> Resguardo de reserva
        </Button>
      </PageHeader>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="data-card p-5 md:col-span-1">
          <div className="flex items-center gap-2 mb-3"><User className="h-4 w-4 text-primary" /><p className="overline">Datos</p></div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">{tipoDocumentoLabel(tenant.tipo_documento)}</dt><dd className="font-mono">{tenant.dni || "—"}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Dirección</dt><dd className="text-right">{tenant.direccion || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Teléfono</dt><dd>{tenant.telefono || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd>{tenant.email || "—"}</dd></div>
          </dl>
        </div>
        <div className="data-card p-5">
          <p className="overline mb-2">Total cobrado</p>
          <p className="font-mono text-2xl font-semibold text-success">{eur(totalCobrado)}</p>
          <p className="text-xs text-muted-foreground mt-1">{payments.filter((p) => p.realizado).length} pagos realizados</p>
        </div>
        <div className="data-card p-5">
          <p className="overline mb-2">Pendiente (vencido)</p>
          <p className="font-mono text-2xl font-semibold text-warning">{eur(pendiente)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {vencidos.length} pago(s) vencido(s) sin cobrar
            {programados.length > 0 && ` · ${programados.length} programado(s) a futuro`}
          </p>
        </div>
      </div>

      {res && (
        <div className={`data-card p-5 mt-4 ${contracts.length > 0 ? "opacity-70" : ""}`} data-testid="reserva-card">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <CalendarCheck className="h-4 w-4 text-primary" /><h3 className="font-display font-medium">Reserva</h3>
            <Badge className={`${estadoReserva.cls} border-0`}>{estadoReserva.label}</Badge>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => generarReserva(res)} disabled={!prop}><Eye className="h-4 w-4 mr-1.5" /> Ver resguardo</Button>
              {contracts.length === 0 && (
                <Button size="sm" onClick={crearContrato} data-testid="reserva-crear-contrato"><Plus className="h-4 w-4 mr-1.5" /> Crear contrato</Button>
              )}
            </div>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
            <div><dt className="text-muted-foreground">Habitación</dt><dd>{res.habitacion ? `Hab. ${res.habitacion}` : "—"}</dd></div>
            <div><dt className="text-muted-foreground">Entrada</dt><dd className="font-mono">{fechaCorta(res.fecha_entrada)}</dd></div>
            <div><dt className="text-muted-foreground">Duración</dt><dd>{res.meses} meses</dd></div>
            <div><dt className="text-muted-foreground">Importe reserva</dt><dd className="font-mono">{eur(res.importe)}</dd></div>
            <div><dt className="text-muted-foreground">Mensualidad</dt><dd className="font-mono">{eur(res.alquiler)}</dd></div>
            <div><dt className="text-muted-foreground">Límite para firmar</dt><dd className="font-mono">{res.fecha_limite ? fechaCorta(res.fecha_limite) : "—"}</dd></div>
          </dl>
        </div>
      )}

      <div className="data-card p-5 mt-4">
        <div className="flex items-center gap-2 mb-3"><FileText className="h-4 w-4 text-primary" /><h3 className="font-display font-medium">Contratos ({contracts.length})</h3></div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Habitación</TableHead><TableHead>Inicio</TableHead><TableHead>Fin</TableHead>
            <TableHead className="text-right">Meses</TableHead><TableHead className="text-right">Alquiler</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {contracts.map((c) => (
              <TableRow key={c.id}>
                <TableCell>Hab. {c.habitacion}</TableCell>
                <TableCell>{fechaCorta(c.fecha_inicio)}</TableCell>
                <TableCell>{fechaCorta(c.fecha_fin)}</TableCell>
                <TableCell className="text-right font-mono">{c.meses}</TableCell>
                <TableCell className="text-right font-mono">{eur(c.alquiler)}</TableCell>
              </TableRow>
            ))}
            {contracts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground">Sin contratos</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <div className="data-card p-5 mt-4">
        <div className="flex items-center gap-2 mb-3"><Wallet className="h-4 w-4 text-primary" /><h3 className="font-display font-medium">Historial de pagos ({payments.length})</h3></div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Fecha</TableHead><TableHead>Concepto</TableHead>
            <TableHead className="text-right">Importe</TableHead><TableHead className="text-right">Estado</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-sm">{fechaCorta(p.fecha)}</TableCell>
                <TableCell className="text-muted-foreground">{p.concepto}</TableCell>
                <TableCell className="text-right font-mono">{eur(p.importe)}</TableCell>
                <TableCell className="text-right">
                  {p.realizado
                    ? <Badge className="bg-success/15 text-success hover:bg-success/15 border-0">Pagado</Badge>
                    : <Badge className="bg-warning/15 text-warning hover:bg-warning/15 border-0">Pendiente</Badge>}
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground">Sin pagos</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Datos del resguardo de reserva */}
      <Dialog open={!!reserva} onOpenChange={(o) => !o && setReserva(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Resguardo de reserva</DialogTitle>
            <DialogDescription>Documento para entregar al inquilino. El texto sale de Plantilla › Reserva.</DialogDescription>
          </DialogHeader>
          {faltanDatosPiso.length > 0 && (
            <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" data-testid="reserva-faltan-datos">
              No se puede generar: faltan <strong>{faltanDatosPiso.join(", ")}</strong>.
              {" "}Los del piso se rellenan en <button type="button" className="underline text-primary" onClick={() => navigate("/pisos")}>Pisos</button> (editar {prop?.nombre || "el piso"}); el documento del inquilino, editando su ficha en Inquilinos.
            </div>
          )}
          {reserva && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Habitación</Label>
                {(prop?.habitaciones || []).length > 0 ? (
                  <Select value={reserva.habitacion} onValueChange={(v) => setReserva((r) => ({ ...r, habitacion: v }))}>
                    <SelectTrigger data-testid="reserva-habitacion"><SelectValue placeholder="Elige" /></SelectTrigger>
                    <SelectContent>
                      {prop.habitaciones.map((h) => <SelectItem key={h.numero} value={String(h.numero)}>Hab. {h.numero}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={reserva.habitacion} onChange={setR("habitacion")} />
                )}
              </div>
              <div><Label>Importe reserva (€)</Label><Input type="number" min="0" value={reserva.importe} onChange={setR("importe")} data-testid="reserva-importe" /></div>
              <div><Label>Fecha de entrada</Label><Input type="date" value={reserva.fecha_entrada} onChange={setR("fecha_entrada")} data-testid="reserva-fecha-entrada" /></div>
              <div><Label>Duración (meses)</Label><Input type="number" min="1" value={reserva.meses} onChange={setR("meses")} /></div>
              <div><Label>Mensualidad con gastos (€)</Label><Input type="number" min="0" value={reserva.alquiler} onChange={setR("alquiler")} /></div>
              <div><Label>Fecha límite para firmar</Label><Input type="date" value={reserva.fecha_limite} onChange={setR("fecha_limite")} /></div>
              <div><Label>Fecha del documento</Label><Input type="date" value={reserva.fecha_reserva} onChange={(e) => {
                const v = e.target.value;
                // El plazo para formalizar se cuenta desde la fecha del documento
                setReserva((r) => ({ ...r, fecha_reserva: v, fecha_limite: v ? sumarDias(v, PLAZO_FORMALIZAR_DIAS) : r.fecha_limite }));
              }} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReserva(null)}>Cancelar</Button>
            <Button onClick={() => generarReserva()} disabled={faltanDatosPiso.length > 0} data-testid="generar-reserva-button">Generar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vista previa del resguardo */}
      <Dialog open={!!docHtml} onOpenChange={(o) => !o && setDocHtml("")}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center justify-between pr-12">
              Resguardo de reserva
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openSignedPdf} disabled={pdfSaving}><Download className="h-4 w-4 mr-1.5" /> Ver PDF firmado</Button>
                <Button size="sm" onClick={printDoc}><Printer className="h-4 w-4 mr-1.5" /> Imprimir / PDF</Button>
              </div>
            </DialogTitle>
            <DialogDescription>Vista previa con formato.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto bg-white rounded-md border border-border p-10" data-testid="reserva-document-preview">
            <div className="contract-render" dangerouslySetInnerHTML={{ __html: docHtml }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
